import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import { format } from "date-fns";
import check from "../../assets/check.svg";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDashboard } from '../Context/Dashboardcontext';
import { getAllClients, addClient, deleteDriver, updateDriver } from '../../ApiController/ApiService';
import { uploadFileToCloudinary } from '../../Utils/uploadFileToCloudinary';
import Swal from 'sweetalert2';

const ClientManagement = () => {
  const { t } = useTranslation();

  // 2. Get userProfile from Context
  const { userProfile } = useDashboard();

  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 1,
    totalDocs: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);

  const initialFormState = {
    fullName: '',
    companyType: 'Dispax',
    city: '',
    state: '',
    country: '',
    companyRegistrationNumber: '',
    email: '',
    mobileNumber: '',
    websiteUrl: '',
    image: null,
    Id: '', // Keeping existing ID field if needed
    address: '', // Keeping existing address field
  };

  const [clientForm, setClientForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchClients = async (page = 1) => {
    // Optional: Avoid fetching if userProfile isn't loaded yet to prevent wrong filtering
    // if (!userProfile) return; 

    setIsLoading(true);
    setError(null);

    try {
      const res = await getAllClients({ page });
      console.log('getAllClients', res);

      if (res.success && Array.isArray(res.data.data.docs)) {
        const allClients = res.data.data.docs;
        let filteredClients = allClients;

        // 3. Conditional Filtering Logic
        if (userProfile?.aiPackageId) {
          // If AI Package exists -> Show ALL (No Filter)
          filteredClients = allClients;
        } else {
          // Else -> Filter by 'Dispax'
          filteredClients = allClients.filter(client => client?.companyType === 'Dispax');
        }

        setClients(filteredClients);

        setPaginationInfo({
          totalPages: res.data.data.totalPages,
          totalDocs: res.data.data.totalDocs,
          hasNextPage: res.data.data.hasNextPage,
          hasPrevPage: res.data.data.hasPrevPage,
        });
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 4. Add userProfile to dependency array so it re-runs when profile loads
  useEffect(() => {
    fetchClients(currentPage);
  }, [currentPage, userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setClientForm(prev => ({ ...prev, [name]: files[0] }));
    }
  };

  const handleEditClient = (client) => {
    setIsEditMode(true);
    setEditingClientId(client._id);
    setSelectedClient(client);
    setClientForm({
      fullName: client.fullName || '',
      companyType: client.companyType || 'Dispax',
      city: client.city || '',
      state: client.state || '',
      country: client.country || '',
      companyRegistrationNumber: client.companyRegistrationNumber || '',
      email: client.email || '',
      mobileNumber: client.mobileNumber || '',
      websiteUrl: client.websiteUrl || '',
      image: null, // Reset image on edit unless we want to show preview of existing
      Id: client.Id || '',
      address: client.address || '',
    });
    setShowModal(true);
  };

  const handleDeleteClient = async (e, clientId) => {
    e.stopPropagation(); // Prevent row click
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDriver(clientId);
        Swal.fire('Deleted!', 'Client has been deleted.', 'success');
        fetchClients(currentPage);
      } catch (err) {
        Swal.fire('Error!', err.message || 'Failed to delete client', 'error');
      }
    }
  };

  const handleSubmitClient = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!clientForm.fullName || !clientForm.email || !clientForm.mobileNumber) {
        Swal.fire({
          icon: 'warning',
          title: 'Incomplete Form',
          text: 'Please fill all required fields',
        });
        setIsSubmitting(false);
        return;
      }

      let imageUrl = selectedClient?.image || '';
      if (clientForm.image instanceof File) {
        imageUrl = await uploadFileToCloudinary(
          clientForm.image,
          `clients/${clientForm.email || clientForm.fullName || 'client'}`
        );
      }

      const formData = new FormData();
      Object.entries(clientForm).forEach(([key, value]) => {
        if (key === 'image') {
          if (imageUrl) {
            formData.append('image', imageUrl);
          }
          return;
        }

        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });

      let response;
      if (isEditMode) {
        response = await updateDriver(editingClientId, formData);
      } else {
        response = await addClient(formData);
      }

      if (response.success === false) {
        Swal.fire({
          icon: 'error',
          title: isEditMode ? 'Failed to Update Client' : 'Failed to Add Client',
          text: response.message || 'Something went wrong',
        });
        setIsSubmitting(false);
        return;
      }

      Swal.fire({
        icon: 'success',
        title: isEditMode ? 'Client Updated' : 'Client Added',
        text: isEditMode ? 'Client updated successfully!' : 'Client added successfully!',
        confirmButtonText: 'OK'
      });

      setShowModal(false);
      if (!isEditMode) setIsSuccessModalOpen(true);
      setSelectedClient(null);

      if (currentPage !== 1 && !isEditMode) {
        setCurrentPage(1);
      } else {
        fetchClients(currentPage);
      }

      setClientForm(initialFormState);
      setIsEditMode(false);
      setEditingClientId(null);

    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to process request',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNextPage) setCurrentPage(p => p + 1);
  };
  const handlePrevPage = () => {
    if (paginationInfo.hasPrevPage) setCurrentPage(p => p - 1);
  };

  return (
    <div className="text-white relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-semibold">{t('client.title') || 'Client Management'}</h1>
      </div>

      {/* Add Client Button */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-end mb-6 gap-4">
        <button
          onClick={() => {
            setIsEditMode(false);
            setClientForm(initialFormState);
            setShowModal(true);
          }}
          className="ml-0 md:ml-4 bg-gradient-to-r from-[#6E00FF] to-[#A627FF] hover:opacity-90 px-4 py-2 md:px-5 md:py-2 rounded-xl text-white font-medium flex items-center gap-2 shadow-md w-full md:w-auto justify-center"
        >
          <FiPlus className="text-md" />
          {t('client.addButton') || 'Add Client'}
        </button>
      </div>

      {/* Clients Table */}
      <div className="overflow-x-auto bg-[#1D2461] rounded-lg">
        <table className="w-full table-auto text-sm text-left">
          <thead className="bg-[#B2BAFF] text-[#202224] uppercase text-[12px] md:text-[14px]">
            <tr>
              <th className="p-2 md:p-4">#</th>
              <th className="p-2 md:p-4">Name</th>
              <th className="p-2 md:p-4 hidden md:table-cell">Email</th>
              <th className="p-2 md:p-4">Mobile</th>
              <th className="p-2 md:p-4 hidden md:table-cell">ID</th>
              <th className="p-2 md:p-4 hidden md:table-cell">Status</th>
              <th className="p-2 md:p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="7" className="p-4 text-center text-gray-400">Loading...</td></tr>
            ) : clients.length > 0 ? (
              clients.map((client, idx) => (
                <tr
                  key={client._id || idx}
                  className="border-t border-[#2A2A4D] hover:bg-[#2A306C] cursor-pointer"
                  onClick={() => {
                    setSelectedClient(client);
                    setShowModal2(true);
                  }}
                >
                  <td className="p-2 md:p-4">{idx + 1}</td>
                  <td className="p-2 md:p-4 flex items-center gap-2">
                    <img
                      src={client.image || "https://ui-avatars.com/api/?name=User&background=random"}
                      alt={client.fullName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    {client.fullName}
                  </td>
                  <td className="p-2 md:p-4 hidden md:table-cell">{client.email}</td>
                  <td className="p-2 md:p-4">{client.mobileNumber}</td>
                  <td className="p-2 md:p-4 hidden md:table-cell">{client.Id || '—'}</td>
                  <td className="p-2 md:p-4 hidden md:table-cell">{client.userStatus || 'N/A'}</td>
                  <td className="p-2 md:p-4">
                    <div className="flex gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClient(client);
                        }}
                        className="text-blue-400 hover:text-blue-200" title="Edit"
                      >
                        <FiEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClient(e, client._id)}
                        className="text-red-400 hover:text-red-200" title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="p-4 text-center text-gray-400">No clients found</td></tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-gray-400 p-2 md:p-4 border-t border-[#2A2A4D]">
          <div>Showing {clients.length} of {paginationInfo.totalDocs}</div>
          <div className="flex items-center gap-4">
            <button onClick={handlePrevPage} disabled={!paginationInfo.hasPrevPage} className="px-3 py-1 rounded disabled:opacity-50 hover:bg-[#2A306C]">Previous</button>
            <span>Page {currentPage} of {paginationInfo.totalPages}</span>
            <button onClick={handleNextPage} disabled={!paginationInfo.hasNextPage} className="px-3 py-1 rounded disabled:opacity-50 hover:bg-[#2A306C]">Next</button>
          </div>
        </div>
      </div>

      {/* Add Client Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
          <div
            className="rounded-xl shadow-xl text-white p-4 md:p-6 relative w-full max-w-[480px] max-h-[90vh] overflow-y-auto hide-scrollbar"
            style={{ background: "radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)" }}
          >
            <button onClick={() => setShowModal(false)} className="absolute top-2 right-4 text-2xl font-bold">×</button>
            <h2 className="text-xl font-bold mb-4">{isEditMode ? 'Edit Client' : (t('client.addTitle') || 'Add New Client')}</h2>

            <form className="space-y-4" onSubmit={handleSubmitClient}>
              {[
                { name: 'fullName', label: 'Full Name', type: 'text' },
                { name: 'companyType', label: 'Company Type', type: 'text' },
                { name: 'companyRegistrationNumber', label: 'Registration Number', type: 'text' },
                { name: 'email', label: 'Email', type: 'email' },
                { name: 'mobileNumber', label: 'Mobile Number', type: 'text' },
                { name: 'websiteUrl', label: 'Website URL', type: 'text' },
                { name: 'city', label: 'City', type: 'text' },
                { name: 'state', label: 'State', type: 'text' },
                { name: 'country', label: 'Country', type: 'text' },
                { name: 'Id', label: 'Client ID', type: 'text' },
                { name: 'address', label: 'Address', type: 'text' },
              ].map((field) => (
                <div key={field.name}>
                  <label className="text-sm block mb-1">{field.label}</label>
                  <input
                    name={field.name}
                    type={field.type}
                    value={clientForm[field.name]}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 rounded bg-white text-black"
                    placeholder={`Enter ${field.label}`}
                  />
                </div>
              ))}

              <div>
                <label className="text-sm block mb-1 text-white">Client Image</label>
                <div className="bg-white rounded-lg p-3">
                  {clientForm.image ? (
                    <div className="mb-2 relative">
                      <img
                        src={URL.createObjectURL(clientForm.image)}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setClientForm(prev => ({ ...prev, image: null }))}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"
                      >×</button>
                    </div>
                  ) : selectedClient?.image && isEditMode ? (
                    <div className="mb-2">
                      <img
                        src={selectedClient.image}
                        alt="Current"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  ) : null}
                  <input
                    type="file"
                    name="image"
                    onChange={handleFileChange}
                    className="w-full text-sm text-black"
                    accept="image/*"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="mt-6 w-full bg-[#792aff] text-white py-2 rounded-md font-semibold disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : (isEditMode ? 'Update Client' : (t('client.submit') || 'Save'))}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-2">
          <div className="bg-[#0F0D2E] p-6 rounded-2xl w-full max-w-md text-center">
            <h2 className="text-xl font-bold text-white">Client Added Successfully</h2>
            <div className="flex justify-center my-4">
              <img src={check} alt="success" className="w-24 h-20" />
            </div>
            <button
              onClick={() => setIsSuccessModalOpen(false)}
              className="w-full py-2 bg-[#6F1AFF] text-white rounded-xl hover:opacity-90 transition font-medium"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* View Client Modal */}
      {showModal2 && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
          <div
            className="rounded-xl shadow-xl text-white p-4 md:p-6 relative w-full max-w-[480px] max-h-[90vh] overflow-y-auto hide-scrollbar"
            style={{ background: "radial-gradient(50% 49.39% at 50% 50%, #1D2461 0%, #080625 100%)" }}
          >
            <button onClick={() => setShowModal2(false)} className="absolute top-2 right-4 text-2xl font-bold">×</button>
            <h2 className="text-xl font-bold mb-4">Client Details</h2>

            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <img
                  src={selectedClient.image || "https://ui-avatars.com/api/?name=User&background=random"}
                  alt={selectedClient.fullName}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <p><strong>Full Name:</strong> {selectedClient.fullName}</p>
                  <p><strong>Status:</strong> {selectedClient.userStatus || 'N/A'}</p>
                </div>
              </div>
              <p><strong>Email:</strong> {selectedClient.email}</p>
              <p><strong>Mobile:</strong> {selectedClient.mobileNumber}</p>
              <p><strong>ID:</strong> {selectedClient.Id || '—'}</p>
              <p><strong>Address:</strong> {selectedClient.address || '—'}</p>
              <p><strong>Joined:</strong> {selectedClient.createdAt ? format(new Date(selectedClient.createdAt), 'PPp') : 'N/A'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
